import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/** คะแนน 1–4 (ข้อมูลเก่าที่เป็น 5 จะแสดงเป็นดาวเต็ม 4) */
export const FEEDBACK_MAX_STARS = 4;

export interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  readonly?: boolean;
  className?: string;
  /** แสดง น้อย ใต้ดาวแรก, มาก ใต้ดาวสุดท้าย */
  showScaleLabels?: boolean;
}

export function StarRating({
  value,
  onChange,
  disabled = false,
  readonly = false,
  className,
  showScaleLabels = false,
}: Readonly<StarRatingProps>) {
  const interactive = !disabled && !readonly && typeof onChange === 'function';
  const displayValue = Math.min(Math.max(value, 0), FEEDBACK_MAX_STARS);

  const starButtons = Array.from({ length: FEEDBACK_MAX_STARS }, (_, i) => {
    const starValue = i + 1;
    const filled = displayValue >= starValue;
    return (
      <button
        key={starValue}
        type="button"
        disabled={disabled || readonly}
        onClick={() => interactive && onChange(starValue)}
        onKeyDown={(e) => {
          if (!interactive) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(starValue);
          }
        }}
        className={cn(
          'p-0.5 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          interactive && 'cursor-pointer hover:opacity-90',
          (disabled || readonly) && 'cursor-default'
        )}
        aria-label={`${starValue} ดาว`}
        aria-pressed={filled}
      >
        <Star
          className={cn(
            'h-8 w-8 sm:h-9 sm:w-9',
            filled
              ? 'fill-amber-400 text-amber-500 dark:fill-amber-500 dark:text-amber-400'
              : 'fill-muted text-muted-foreground/50'
          )}
        />
      </button>
    );
  });

  if (showScaleLabels) {
    return (
      <fieldset className={cn('m-0 w-full border-0 p-0', className)}>
        <legend className="sr-only">
          คะแนน {displayValue} จาก {FEEDBACK_MAX_STARS}
        </legend>
        <div className="mx-auto flex w-fit flex-col items-stretch gap-0.5">
          <div className="flex justify-center gap-0">{starButtons}</div>
          <div className="grid grid-cols-4 text-center text-xs text-muted-foreground sm:text-sm">
            <span>น้อย</span>
            <span aria-hidden className="select-none opacity-0">
              ·
            </span>
            <span aria-hidden className="select-none opacity-0">
              ·
            </span>
            <span>มาก</span>
          </div>
        </div>
      </fieldset>
    );
  }

  return (
    <fieldset
      className={cn('flex gap-0.5 border-0 p-0 m-0 min-w-0 items-center', className)}
      aria-label={`คะแนน ${displayValue} จาก ${FEEDBACK_MAX_STARS}`}
    >
      {starButtons}
    </fieldset>
  );
}
